import type { PageContent } from "../types";

export const p_how_to_hire_an_seo_consultant: PageContent = {
  slug: "how-to-hire-an-seo-consultant",
  title: `How to Hire an SEO Consultant: Questions to Ask Before You Sign | Phil Carr`,
  description: `A practical checklist of questions to ask an SEO consultant before you hire them — covering scope, reporting, pricing, and red flags to avoid.`,
  date: "2026-06-15",
  body: `# How to Hire an SEO Consultant: Questions to Ask Before You Sign

**Published:** June 15, 2026

## TL;DR

Hiring the right SEO consultant comes down to asking the right questions before you sign — about scope, reporting, pricing, and the specific results they've delivered for businesses like yours. Use the checklist below in your first conversation; anyone worth working with will answer every question plainly, without deflecting into jargon.

Hiring an SEO consultant is one of the easier ways to waste money if you go in without a checklist. The market is full of vague proposals, padded retainers, and reports that don't tie to anything you actually care about. This is a straight-talking guide to what to ask before you sign — built around the questions real business owners send me every week.

## Start with what you're actually buying

Before you ask anyone anything, get clear on the outcome. SEO isn't a single product. It's usually one of:

- a one-off audit that tells you what's wrong and how to fix it,
- a fixed-scope project (technical fixes, a content rebuild, migration support),
- or an ongoing retainer that combines strategy, content, and links.

The same consultant might do all three, but the pricing, deliverables, and timelines are completely different. Don't ask "how much for SEO?" — ask "how much for the specific outcome I need?"

### 1. What does your work actually include?

Get the deliverables in writing. A good answer names specific work: technical audit, page-by-page recommendations, content briefs, link outreach, GA4 and Search Console reporting. A bad answer is "we do SEO."

### 2. What does success look like in the first 90 days?

You're not asking for guaranteed rankings — anyone who promises those is lying. You're asking what the consultant will have done in 90 days and what early signals they expect: indexing improvements, impressions growth, rankings on a target set of keywords, conversion-rate gains on key pages.

### 3. How do you decide what to work on first?

The answer should reference your revenue, your highest-intent pages, and a technical baseline. If they jump straight to "we'll build backlinks," walk away.

### 4. Who actually does the work?

Find out whether the person on the call is the person doing the work, whether anything is outsourced, and where. This matters for quality, NDAs, and accountability.

### 5. What tools and data sources do you use?

Expect Google Search Console, GA4, and one of the major SEO platforms (Semrush, Ahrefs, Sitebulb, Screaming Frog). If they can't name their stack clearly, they probably don't have one.

### 6. How will you report progress, and how often?

Monthly is standard. Ask to see a sample report. Look for: traffic by landing page, keyword movement on a tracked set, technical issues opened and closed, and a written summary of what was done and what's next.

### 7. What does a typical engagement cost?

Get the pricing model in writing — fixed-fee project, monthly retainer, or hourly. Ask what's not included (content writing, dev work, paid tools, link costs).

### 8. What's your approach to link building?

You want to hear "relevant, editorial, slow." You don't want to hear "PBNs," "guaranteed DA50+ links," or anything involving bulk packages.

### 9. Can I see two or three examples of similar work?

Case studies should name the situation, the work done, and the result — ideally with screenshots from Search Console or GA4. Vague "we grew traffic 300%" claims with no context are worthless.

### 10. What happens if we want to stop?

Notice periods, ownership of deliverables, access to accounts, and what you keep when the engagement ends. A confident consultant has a clean offboarding answer.

## Red flags to watch for

- **Guaranteed #1 rankings.** Nobody can guarantee that.
- **No access to your analytics or Search Console.** SEO without data is guesswork.
- **Long lock-ins with no clear deliverables.** Month-to-month with a notice period is reasonable. A 12-month contract with "we'll do SEO" is not.
- **They won't tell you what they'll publish or where they'll get links.**
- **Reporting that's all vanity metrics** — keyword counts, "impressions up" — with no link to enquiries or revenue.

## What to send a consultant before the first call

Save everyone time and send these up front:

- Your website URL and the two or three pages that matter most commercially.
- Read-only access (or a screenshot) of GA4 and Search Console for the last six months.
- Your top three competitors.
- A one-line goal: "more quote requests from Barnsley," "rank for [service] in [city]," "recover from the May 2026 core update."

With that, a consultant should be able to give you a useful opinion on the first call — not a sales pitch.

## Hiring locally vs. nationally

For local businesses — trades, clinics, solicitors, accountants — there's real value in a consultant who understands the local market, the competitor set, and how Google's local pack behaves in your area. For a national e-commerce site, geography matters far less than experience in your category.

If you're a Yorkshire business, my [areas covered](/areas) page lists the towns and cities I work across, and the [services page](/services) walks through what each engagement type includes.

## A simple decision rule

After the conversation, ask yourself two questions:

1. Could I explain, in one sentence, what they're going to do in the first month?
2. Do I trust them to tell me the truth when something isn't working?

If the answer to either is no, keep looking. Good SEO compounds over months and years — the cost of hiring the wrong consultant isn't just the fee, it's the lost time.

If you'd like a straight opinion on whether SEO is the right next move for your business, [get in touch](/contact) — no sales pitch, just a practical conversation.

## About the author

Phil Carr is an independent SEO consultant and web strategist in Barnsley, South Yorkshire, helping small and mid-sized UK businesses grow through practical SEO, content, and conversion-focused web design.
`,
};
